import axios from 'axios';

// Each service owns its axios instance, matching the rest of the app.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const projectId = localStorage.getItem('currentProjectId');
  if (projectId) config.headers['X-Project-Id'] = projectId;
  return config;
});

const prospectService = {
  async list({ page = 1, limit = 50, search, searchField, emailStatus, source, signal, list, contacted, sort, direction } = {}) {
    const { data } = await api.get('/prospects', {
      params: {
        page, limit, search: search || undefined, emailStatus, source,
        searchField: searchField || undefined,
        signal: signal || undefined, list: list || undefined,
        contacted: contacted || undefined,
        sort: sort || undefined, direction: direction || undefined
      }
    });
    return data.data;
  },

  /**
   * Every id matching the filters currently applied.
   *
   * So Select all can mean all of them rather than the fifty on screen. The
   * ids come back here and go out with the action, so it runs on exactly the
   * set the person was shown a count for.
   */
  async matchingIds(params = {}) {
    const { data } = await api.get('/prospects/ids', { params });
    return data.data;
  },

  /**
   * Every matching lead as a CSV, from the server.
   *
   * This used to be assembled in the browser from the rows already loaded, so
   * it exported the page rather than the selection. The columns are the ones
   * the importer reads, so the file uploads back into Orion unedited.
   */
  async exportCsv(params = {}) {
    const { data } = await api.get('/prospects/export', { params, responseType: 'blob' });
    return data;
  },

  /** Stops all future outreach to these people, without deleting them. */
  async suppress(prospectIds) {
    const { data } = await api.post('/prospects/suppress', { prospectIds });
    return data.data;
  },

  /** The pools leads are grouped into. */
  async lists() {
    const { data } = await api.get('/prospects/lists');
    return data.data.lists;
  },

  /** Corrects a lead by hand. Only the fields passed are touched. */
  async updateLead(id, changes) {
    const { data } = await api.patch(`/prospects/${id}`, changes);
    return data.data.lead;
  },

  /** Removes a lead. Refused with a reason for anyone already contacted. */
  async deleteLead(id) {
    const { data } = await api.delete(`/prospects/${id}`);
    return data;
  },

  async renameList(id, name) {
    const { data } = await api.patch(`/prospects/lists/${id}`, { name });
    return data.data.list;
  },

  /** Makes a pool out of the leads currently selected. */
  async createList(name, prospectIds) {
    const { data } = await api.post('/prospects/lists', { name, prospectIds });
    return data.data;
  },

  async addToList(listId, prospectIds) {
    const { data } = await api.post(`/prospects/lists/${listId}/members`, { prospectIds });
    return data.data;
  },

  /** Deletes a pool. The people in it stay on the ledger. */
  async deleteList(listId) {
    const { data } = await api.delete(`/prospects/lists/${listId}`);
    return data;
  },

  async stats() {
    const { data } = await api.get('/prospects/stats');
    return data.data;
  },

  /** Which finders are connected, and what each has left. */
  async providers() {
    const { data } = await api.get('/prospects/providers');
    return data.data.providers || [];
  },

  /**
   * @param {string} provider a specific finder, or 'all' for the usual order
   * @param {boolean} force ask again even if a provider was tried recently
   */
  async enrich(prospectIds, { provider = 'all', force = false, capabilities = ['work_email'] } = {}) {
    const { data } = await api.post('/prospects/enrich', { prospectIds, provider, force, capabilities });
    return data.data;
  },

  /** What a run would cost, so the cost is shown before it is spent. */
  async estimateEnrichment(prospectIds, capabilities) {
    const { data } = await api.post('/prospects/enrich/estimate', { prospectIds, capabilities });
    return data.data;
  }
};

export default prospectService;
